import React, { useCallback, useEffect } from 'react';
import Input from 'shared/components/input/Input';
import Select from 'shared/components/select/Select';
import Switch from 'shared/components/switch/Switch';
import DayPartFields from './DayPartFields';
import ExcludeAvailsFields from './ExcludeAvailsFields';
import IncludeAvailsFields from './IncludeAvailsFields';
import { plusCircleAltIcon } from 'assets';
import { trashcanGreenIcon } from 'shared/assets';
import {
  allow1Point,
  allow2Decimals,
  formatDecimal,
  getLastIdFromArray,
  handleTrimNumeric,
  replaceWithInt,
} from '../../helpers/helpers';
import useStore from '../../hooks/useCampaignStore';
import CAMPAIGN_OBJECTIVE from 'enums/CampaignObjectiveEnum';
import AuthService from 'core/auth.service';

const ParameterFields = (props) => {
  const {
    availsExcludes,
    bonusSpots,
    bookingPeriods,
    bookingPeriodErrors,
    bookingPeriodList,
    dayWeekList,
    dayWeekRows,
    dayPartList,
    dayPartRows,
    dayWeekErrors,
    dayPartErrors,
    handleScrollFormToBottom,
    includeAvails,
    includeClientNameInOrderAndTraffic,
    limits,
    limitErrors,
    setAvailsExcludes,
    setBonusSpots,
    setBookingPeriods,
    setDayWeekRows,
    setDayPartRows,
    setDayWeekErrors,
    setDayPartErrors,
    setIncludeAvails,
    setIncludeClientNameInOrderAndTraffic,
    setLimits,
    selectedCampaign,
    setValidDaypartFields,
  } = props;

  const { isCampaignActive, isFormTouched, setIsFormTouched } = useStore(
    (state) => state
  );

  // Auth Service and User Identity
  const userIdentity = AuthService.getUserIdentity();

  // Agency Variables and State
  const isOrganisationAgency = userIdentity.organisationType === 2;

  const radioType = 2;
  const isCampaignTypeRadio = selectedCampaign?.type === radioType;
  const customDaypartId = isCampaignTypeRadio ? 36 : 35;
  const isCampaignPlacedOrReach =
    selectedCampaign?.objective === CAMPAIGN_OBJECTIVE.placed ||
    selectedCampaign?.objective === CAMPAIGN_OBJECTIVE.reach;

  // Input Handling
  const handleOnDayWeekSelect = (dayPosition, selectedRow, data) => {
    const {
      startDayId: currentStartDayId,
      endDayId: currentEndDayId,
    } = selectedRow;
    const selectedDayId = data.id;

    setIsFormTouched(true);

    setDayWeekRows((prevRow) =>
      prevRow.map((row) => {
        if (row.id !== selectedRow.id) return row;

        switch (dayPosition) {
          case 'START':
            if (currentEndDayId && selectedDayId > currentEndDayId) {
              return {
                ...row,
                startDayId: selectedDayId,
                endDayId: selectedDayId,
              };
            }
            return { ...row, startDayId: selectedDayId };
          case 'END':
            if (currentStartDayId && selectedDayId < currentStartDayId) {
              return {
                ...row,
                startDayId: selectedDayId,
                endDayId: selectedDayId,
              };
            }
            return { ...row, endDayId: selectedDayId };
          default:
            return row;
        }
      })
    );
  };

  const handleOnAllocationChange = useCallback(
    (rowName, selectedId, e, rowData) => {
      setIsFormTouched(true);

      let val = e.target.value;
      const maxValue = 100;

      if (val.length > 1) val = val.replace(/^0+/, '');

      if (val > maxValue) return;
      val = val.replace('..', '.');

      if (val.includes('.')) {
        const valSplit = val.split('.');
        if (valSplit[0] === '') val = `0${val}`;
      }

      e.target.value = val.replace(/^00+/, 0);
      e.target.value = allow1Point(allow2Decimals(e, val));

      switch (rowName) {
        case 'DAYWEEK':
          setDayWeekRows((prevRows) =>
            prevRows.map((row) =>
              row.id === selectedId
                ? { ...row, allocation: e.target.value }
                : row
            )
          );
          break;
        case 'DAYPART':
          setDayPartRows((prevRows) => {
            if (selectedId === null) {
              // get the last id or default to 0 if array is empty
              const lastId = getLastIdFromArray(prevRows);

              return [
                ...prevRows,
                {
                  id: lastId + 1,
                  daypartId: rowData.id,
                  allocation: e.target.value,
                },
              ];
            }

            // update existing row
            return prevRows.map((row) =>
              row.id === selectedId
                ? { ...row, daypartId: rowData.id, allocation: e.target.value }
                : row
            );
          });
          break;
        default:
          break;
      }
    },
    [dayWeekRows, dayPartRows]
  );

  const handleOnBookingsChange = (e) => {
    let val = e.target.value;
    setIsFormTouched(true);

    if (e.nativeEvent.inputType === 'insertFromPaste') {
      e.target.value = handleTrimNumeric(e.target.value);
    } else if (e.nativeEvent.inputType === 'insertText') {
      e.target.value = val.replace(/[^0-9]/g, '');
    }

    e.target.value = val.replace(/^00+/, 0);
    e.target.value = replaceWithInt(e.target.value);

    setBookingPeriods((prevItem) => ({
      ...prevItem,
      allocation: parseInt(e.target.value, 10) || null,
    }));
  };

  const handleOnExcludeChange = useCallback(
    (rowName, selectedId, isActive, rowData) => {
      setIsFormTouched(true);

      switch (rowName) {
        case 'DAYWEEK':
          setDayWeekRows((prevRows) =>
            prevRows.map((row) =>
              row.id === selectedId
                ? { ...row, exclude: isActive, allocation: 0 }
                : row
            )
          );
          break;
        case 'DAYPART':
          setDayPartRows((prevRows) => {
            if (selectedId == null) {
              // get the last id or default to 0 if array is empty
              const lastId = getLastIdFromArray(prevRows);

              return [
                ...prevRows,
                {
                  id: lastId + 1,
                  daypartId: rowData.id,
                  exclude: isActive,
                  allocation: null,
                },
              ];
            }

            // update existing row
            return prevRows.map((row) =>
              row.id === selectedId
                ? {
                    ...row,
                    daypartId: rowData.id,
                    exclude: isActive,
                    allocation: null,
                  }
                : row
            );
          });
          break;
        default:
          break;
      }
    },
    [setDayWeekRows, setDayPartRows]
  );

  const handleOnLimitChange = (e) => {
    let val = e.target.value;
    setIsFormTouched(true);

    if (e.nativeEvent.inputType === 'insertFromPaste') {
      e.target.value = handleTrimNumeric(e.target.value);
    } else if (e.nativeEvent.inputType === 'insertText') {
      e.target.value = val.replace(/[^0-9]/g, '');
    }

    e.target.value = val.replace(/^00+/, 0);
    e.target.value = replaceWithInt(e.target.value);

    setLimits((prevItem) => ({
      ...prevItem,
      [e.target.name]: parseInt(e.target.value, 10) || null,
    }));
  };

  const handleOnBonusSpotChange = (e) => {
    let val = e.target.value;
    setIsFormTouched(true);

    if (e.nativeEvent.inputType === 'insertFromPaste') {
      e.target.value = handleTrimNumeric(e.target.value);
    } else if (e.nativeEvent.inputType === 'insertText') {
      e.target.value = val.replace(/[^0-9]/g, '');
    }

    e.target.value = val.replace(/^00+/, 0);
    e.target.value = replaceWithInt(e.target.value);

    setBonusSpots((prevItem) => ({
      ...prevItem,
      [e.target.name]: parseInt(e.target.value, 10) || null,
    }));
  };

  const handleOnIncludeClientNameInOrderAndTraffic= (value) => {
    setIsFormTouched(true);
    setIncludeClientNameInOrderAndTraffic(value);
  };

  const handleTimePeriodSelect = (data) => {
    setIsFormTouched(true);

    setBookingPeriods((prevItem) => ({
      ...prevItem,
      bookingTimePeriodId: data.id,
    }));
  };

  // Row Adding and Removing Functions
  const handleOnAddRows = useCallback((rowName) => {
    if (!isCampaignActive) return;

    switch (rowName) {
      case 'DAYWEEK':
        if (isCampaignTypeRadio) return;
        setDayWeekErrors({});
        setDayWeekRows((prevRows) => [
          ...prevRows,
          {
            id: getLastIdFromArray(prevRows) + 1,
            startDayId: null,
            endDayId: null,
            allocation: 0,
            exclude: false,
          },
        ]);
        break;
      case 'DAYPART':
        setDayPartErrors({});
        setDayPartRows((prevRows) => {
          return [
            ...prevRows,
            {
              id: Math.max(...prevRows.map((item) => item.id)) + 1,
              customDaypartName: '',
              daypartId: customDaypartId,
              allocation: 0,
              startTime: null,
              endTime: null,
              exclude: false,
              startDayId: null,
              endDayId: null,
              customDaypartName: '',
            },
          ];
        });
        break;
      default:
        break;
    }
  }, []);

  const handleOnRemoveRows = useCallback(
    (rowName, indexForRemoval) => {
      if (!isCampaignActive) return;

      setIsFormTouched(true);

      switch (rowName) {
        case 'DAYWEEK':
          if (dayWeekRows.length !== 1) {
            setDayWeekErrors({});
            setDayWeekRows((prevRows) =>
              prevRows.filter((row) => row.id !== indexForRemoval)
            );
          }
          break;
        case 'DAYPART':
          if (dayPartRows.length !== 1) {
            setDayPartErrors({});
            setDayPartRows((prevRows) =>
              prevRows.filter((row) => row.id !== indexForRemoval)
            );
          }
          break;
        default:
          break;
      }
    },
    [dayWeekRows, dayPartRows]
  );

  const handleOnKeyDownAddRows = (rowName, e) => {
    if (e.key === 'Enter' || e.key === 'Space') return handleOnAddRows(rowName);
  };

  const handleOnKeyDownRemoveRows = (rowName, id, e) => {
    if (e.key === 'Enter' || e.key === 'Space')
      return handleOnRemoveRows(rowName, id);
  };

  const handleOnChangeCustomDaypartName = useCallback(
    (rowName, selectedId, e) => {
      setIsFormTouched(true);
      switch (rowName) {
        case 'DAYPART':
          setDayPartRows((prevRows) => {
            if (selectedId == null) {
              // get the last id or default to 0 if array is empty
              const lastId = getLastIdFromArray(prevRows);

              return [
                ...prevRows,
                {
                  id: lastId + 1,
                  daypartId: customDaypartId,
                  customDaypartName: e.target.value,
                },
              ];
            }

            // update existing row
            return prevRows.map((row) =>
              row.id === selectedId
                ? {
                    ...row,
                    daypartId: customDaypartId,
                    customDaypartName: e.target.value,
                  }
                : row
            );
          });
          break;
        default:
          break;
      }
    },
    [setDayWeekRows, setDayPartRows]
  );

  /**
   * Rendering Functions
   *
   * TODO: Consider moving these into their own components in the future
   */
  const renderDayWeekRows = useCallback(() => {
    return dayWeekRows.map((dayWeekRow, rowIndex) => {
      const { id, startDayId, endDayId, allocation, exclude } = dayWeekRow;
      const currentStartDay =
        dayWeekList?.find((day) => day.id === startDayId) || null;
      const currentEndDay =
        dayWeekList?.find((day) => day.id === endDayId) || null;

      return (
        <div key={`dayWeekRow${id}`}>
          <div className="day-week-rows">
            <div style={{ width: '100%', maxWidth: '210px' }}>
              <Select
                text={currentStartDay?.name || 'Please Select'}
                list={dayWeekList}
                noSearch
                onSelect={(e) => handleOnDayWeekSelect('START', dayWeekRow, e)}
                width={'100%'}
                disabled={!isCampaignActive || isCampaignTypeRadio}
              />
            </div>
            <div style={{ width: '100%', maxWidth: '210px' }}>
              <Select
                text={currentEndDay?.name || 'Please Select'}
                list={dayWeekList}
                noSearch
                onSelect={(e) => handleOnDayWeekSelect('END', dayWeekRow, e)}
                width={'100%'}
                disabled={!isCampaignActive || isCampaignTypeRadio}
              />
            </div>
            <Input
              value={!exclude ? allocation : ''}
              maxLength={5}
              onChange={(e) => handleOnAllocationChange('DAYWEEK', id, e)}
              style={{ width: '100%', maxWidth: '120px', color: '#212529' }}
              disabled={
                exclude ||
                !isCampaignActive ||
                isCampaignTypeRadio ||
                isCampaignPlacedOrReach
              }
            />
            <div className="exclude-switch-container">
              <Switch
                active={exclude}
                onSwitch={(isActive) => {
                  handleOnExcludeChange('DAYWEEK', id, isActive);
                }}
                disabled={!isCampaignActive || isCampaignTypeRadio}
              />
            </div>
            {dayWeekRows.length > 1 && (
              <img
                src={trashcanGreenIcon}
                alt="trashcanGreenIcon"
                title={
                  isCampaignActive
                    ? 'Remove day of week distrubtion'
                    : undefined
                }
                className={`action-icon ${
                  !isCampaignActive || isCampaignTypeRadio ? 'disabled' : ''
                }`}
                onClick={() => handleOnRemoveRows('DAYWEEK', id)}
                onKeyDown={(e) => handleOnKeyDownRemoveRows('DAYWEEK', id, e)}
                tabIndex={isCampaignActive || isCampaignTypeRadio ? 0 : -1}
              />
            )}
            {dayWeekRows.length - 1 === rowIndex && (
              <img
                src={plusCircleAltIcon}
                alt="plusCircleAltIcon"
                title={
                  isCampaignActive || isCampaignTypeRadio
                    ? 'Add day of week distrubtion'
                    : undefined
                }
                className={`action-icon ${
                  !isCampaignActive || isCampaignTypeRadio ? 'disabled' : ''
                }`}
                onClick={() => handleOnAddRows('DAYWEEK')}
                onKeyDown={(e) => handleOnKeyDownAddRows('DAYWEEK', e)}
                tabIndex={isCampaignActive || isCampaignTypeRadio ? 0 : -1}
              />
            )}
          </div>
          {dayWeekErrors[id] && (
            <div className="d-flex gap-2 day-week-error-rows">
              <p
                className="w-100 is-danger capitalize"
                style={{ maxWidth: '210px' }}
              >
                {dayWeekErrors[id]?.startDay}
              </p>
              <p
                className="w-100 is-danger capitalize"
                style={{ maxWidth: '210px' }}
              >
                {dayWeekErrors[id]?.endDay}
              </p>
              <p
                className="w-100 is-danger capitalize"
                style={{ maxWidth: '85px' }}
              >
                {dayWeekErrors[id]?.allocation}
              </p>
            </div>
          )}
        </div>
      );
    });
  }, [
    dayWeekErrors,
    dayWeekRows,
    dayWeekList,
    handleOnDayWeekSelect,
    handleOnAllocationChange,
    handleOnAddRows,
    handleOnRemoveRows,
  ]);

  const renderBookingPeriods = useCallback(() => {
    const selectedBookingPeriod =
      bookingPeriodList &&
      bookingPeriodList.find(
        (bookingPeriod) =>
          bookingPeriod.id === bookingPeriods.bookingTimePeriodId
      );

    return (
      <>
        <div className="w-100">
          <Input
            maxLength={7}
            type="text"
            label="Bookings"
            placeholder={
              !isCampaignActive && !bookingPeriods.allocation
                ? ''
                : 'Number of bookings'
            }
            name="allocation"
            value={formatDecimal(bookingPeriods.allocation)}
            onChange={handleOnBookingsChange}
            disabled={!isCampaignActive}
          />
        </div>
        <div className="w-100">
          <p style={{ lineHeight: '16.71px', marginBottom: '5px' }}>
            Time Period
          </p>
          <Select
            errorMessage={bookingPeriodErrors?.bookingTimePeriodError}
            text={selectedBookingPeriod?.name || 'Please Select'}
            list={bookingPeriodList}
            noSearch
            onSelect={handleTimePeriodSelect}
            width="100%"
            disabled={!isCampaignActive}
            onOpen={handleScrollFormToBottom}
          />
        </div>
      </>
    );
  }, [
    bookingPeriods,
    bookingPeriodList,
    bookingPeriodErrors,
    isCampaignActive,
  ]);

  useEffect(() => {
    if (bookingPeriodErrors && Object.keys(bookingPeriodErrors).length) {
      handleScrollFormToBottom();
    }
  }, [bookingPeriodErrors]);

  return (
    <>
      <div className="d-flex flex-column gap-10">
        <div className="w-100 border-bottom d-flex">
          <span>Days of week (DoW)</span>
        </div>
      </div>

      <div className="w-100 d-flex flex-column gap-2">
        <div className="row-label">
          <p className="day-start">Start Day</p>
          <p className="day-end">End Day</p>
          <p className="day-allocation">%</p>
          <p className="day-exclude">Exclude</p>
        </div>
        <div className="rows-container">{renderDayWeekRows()}</div>
      </div>

      <DayPartFields
        dayPartErrors={dayPartErrors}
        dayPartList={dayPartList}
        dayPartRows={dayPartRows}
        handleOnAddRows={handleOnAddRows}
        handleOnAllocationChange={handleOnAllocationChange}
        handleOnExcludeChange={handleOnExcludeChange}
        handleOnKeyDownAddRows={handleOnKeyDownAddRows}
        handleOnKeyDownRemoveRows={handleOnKeyDownRemoveRows}
        handleOnChangeCustomDaypartName={handleOnChangeCustomDaypartName}
        handleOnRemoveRows={handleOnRemoveRows}
        setDayPartRows={setDayPartRows}
        setValidDaypartFields={setValidDaypartFields}
      />

      <ExcludeAvailsFields
        availsExcludes={availsExcludes}
        setAvailsExcludes={setAvailsExcludes}
        isFormTouched={isFormTouched}
        setIsFormTouched={setIsFormTouched}
      />

      <IncludeAvailsFields
        includeAvails={includeAvails}
        setIncludeAvails={setIncludeAvails}
      />

      <div className="d-flex flex-column gap-10 align-items-center">
        <div className="w-100 border-bottom d-flex gap-2">
          <span>Limits</span>
        </div>
      </div>

      <div className="d-flex gap-2">
        <div className="w-100">
          <Input
            errorMessage={limitErrors?.maxSpotsPerProgram}
            maxLength={4}
            label="Maximum number of spots per program"
            placeholder={
              !isCampaignActive && !limits.maxSpotsPerProgram
                ? ''
                : 'Enter a maximum number of spots per program'
            }
            name="maxSpotsPerProgram"
            value={formatDecimal(limits.maxSpotsPerProgram)}
            onChange={handleOnLimitChange}
            disabled={!isCampaignActive}
          />
        </div>
        <div className="w-100">
          <Input
            errorMessage={limitErrors?.maxSpotsPerProgramPerWeek}
            maxLength={4}
            label="Maximum number of spots per program per week"
            placeholder={
              !isCampaignActive && !limits.maxSpotsPerProgramPerWeek
                ? ''
                : 'Enter a maximum number of spots per program per week'
            }
            name="maxSpotsPerProgramPerWeek"
            value={formatDecimal(limits.maxSpotsPerProgramPerWeek)}
            onChange={handleOnLimitChange}
            disabled={!isCampaignActive}
          />
        </div>
      </div>

      <div className="d-flex flex-column gap-10 align-items-center">
        <div className="w-100 border-bottom d-flex gap-2">
          <span>Bookings per time period</span>
        </div>
      </div>

      <div className="d-flex gap-2">{renderBookingPeriods()}</div>

      {/* Bonus Spots */}
      <div className="d-flex flex-column gap-10 align-items-center">
        <div className="w-100 border-bottom d-flex gap-2">
          <span>Bonus Spots</span>
        </div>
      </div>
      <div className="w-50">
        <Input
          maxLength={4}
          label="Automatically add bonus spots"
          placeholder="Number of bonus spots per station"
          name="automaticBonusSpots"
          value={formatDecimal(bonusSpots.automaticBonusSpots)}
          onChange={handleOnBonusSpotChange}
          disabled={!isCampaignActive}
        />
      </div>

      {/* Orders and Traffic Instructions */}
      {isOrganisationAgency && (
        <>
          <div className="d-flex flex-column gap-10 align-items-center">
            <div className="w-100 border-bottom d-flex gap-2">
              <span>Orders and Traffic Instructions</span>
            </div>
          </div>
          <div className="w-50">
            <div className="d-flex gap-3">
              <span>
                Include client name in orders and traffic instructions
              </span>
              <div className="oti-switch">
                <Switch
                  active={includeClientNameInOrderAndTraffic}
                  disabled={!isCampaignActive}
                  onSwitch={(val) =>
                    handleOnIncludeClientNameInOrderAndTraffic(val)
                  }
                />
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default ParameterFields;
